"use client"

import { FormEvent, Suspense, useEffect, useMemo, useState, useTransition } from "react"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import { signIn, useSession } from "next-auth/react"
import {
  KeyRound,
  Loader2,
  LockKeyhole,
  Mail,
  ShieldCheck,
  Store,
  User,
  Users,
  type LucideIcon,
} from "lucide-react"

import { getDefaultPathForRole } from "@/lib/auth/rbac"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AuthBrand, AuthDivider, AuthLayout, GoogleAuthButton } from "@/components/auth/auth-shell"

const DEMO_PASSWORD = "ShiftDemo123!"
const IS_DEMO_MODE = process.env.NEXT_PUBLIC_DEMO_MODE === "true"

const DEMO_ACCOUNTS: ReadonlyArray<{
  label: string
  email: string
  description: string
  icon: LucideIcon
}> = [
  {
    label: "管理者",
    email: "admin@plateone.demo",
    description: "全体設定、店舗横断確認",
    icon: ShieldCheck,
  },
  {
    label: "SV",
    email: "sv@plateone.demo",
    description: "複数店舗の閲覧と支援",
    icon: Users,
  },
  {
    label: "店長",
    email: "i.sato@plateone.demo",
    description: "店舗運営とシフト管理",
    icon: Store,
  },
  {
    label: "スタッフ",
    email: "aya.morimoto@plateone.demo",
    description: "提出ポータルの利用",
    icon: User,
  },
]

function getErrorMessage(error: string | null) {
  switch (error) {
    case "CredentialsSignin":
      return "メールアドレスまたはパスワードが正しくありません。"
    case "MFA_REQUIRED":
      return "認証アプリの6桁コードを入力してください。"
    case "MFA_INVALID":
      return "認証コードが正しくありません。"
    case "OAuthAccountNotLinked":
      return "このメールアドレスは既にパスワードで登録されています。メールアドレスとパスワードでログインしてください。"
    default:
      return error ? "ログインに失敗しました。" : ""
  }
}

function LoginPageContent() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { data: session } = useSession()
  const [email, setEmail] = useState<string>(IS_DEMO_MODE ? DEMO_ACCOUNTS[2].email : "")
  const [password, setPassword] = useState<string>(IS_DEMO_MODE ? DEMO_PASSWORD : "")
  const [mfaCode, setMfaCode] = useState("")
  const [requiresMfa, setRequiresMfa] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [isPending, startTransition] = useTransition()

  const callbackUrl = useMemo(() => searchParams.get("callbackUrl") || "/", [searchParams])

  useEffect(() => {
    if (!session?.user) return
    // Plate One 運営者は（自分のテナントを持たなくても）運営コンソールへ。
    if (session.user.isPlatformOperator) {
      router.replace("/platform")
      return
    }
    router.replace(getDefaultPathForRole(session.user.role))
  }, [router, session])

  const searchError = getErrorMessage(searchParams.get("error"))

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setErrorMessage("")

    startTransition(async () => {
      const result = await signIn("credentials", {
        email,
        password,
        mfaCode,
        redirect: false,
        callbackUrl,
      })

      if (!result || result.error) {
        const error = result?.error ?? "CredentialsSignin"
        if (error === "MFA_REQUIRED" || error === "MFA_INVALID") {
          setRequiresMfa(true)
        }
        setErrorMessage(getErrorMessage(error))
        return
      }

      router.push(result.url ?? callbackUrl)
      router.refresh()
    })
  }

  return (
    <AuthLayout>
      <AuthBrand />

      <Card className="rounded-xl border-border/70 shadow-lg">
        <CardHeader className="space-y-1.5 pb-4">
          <CardTitle className="text-xl">ログイン</CardTitle>
          <CardDescription>
            {IS_DEMO_MODE
              ? "運営ロールとスタッフロールを切り替えて確認できます。"
              : "メールアドレスとパスワードでログインしてください。"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {(searchError || errorMessage) && (
            <Alert variant="destructive">
              <AlertTitle>ログインできませんでした</AlertTitle>
              <AlertDescription>{errorMessage || searchError}</AlertDescription>
            </Alert>
          )}

          {!IS_DEMO_MODE && (
            <>
              <GoogleAuthButton onClick={() => signIn("google", { callbackUrl })}>
                Googleでログイン
              </GoogleAuthButton>
              <AuthDivider />
            </>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="email">メールアドレス<span className="ml-0.5 text-red-500" aria-label="必須">*</span></Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="pl-9"
                  placeholder="例: you@example.com"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">パスワード<span className="ml-0.5 text-red-500" aria-label="必須">*</span></Label>
              <div className="relative">
                <LockKeyhole className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="pl-9"
                  required
                />
              </div>
            </div>

            {requiresMfa && (
              <div className="space-y-2">
                <Label htmlFor="mfaCode">認証コード</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="mfaCode"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={mfaCode}
                    onChange={(event) => setMfaCode(event.target.value)}
                    className="pl-9"
                    placeholder="例: 123456"
                  />
                </div>
              </div>
            )}

            <Button
              className="h-11 w-full"
              type="submit"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ログイン中
                </>
              ) : (
                "ログインする"
              )}
            </Button>
          </form>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
            <Link
              href="/login/forgot-password"
              className="text-sm font-medium text-foreground underline underline-offset-4 hover:text-muted-foreground"
            >
              パスワードをお忘れの方
            </Link>
            <Link
              href="/signup"
              className="text-sm font-medium text-foreground underline underline-offset-4 hover:text-muted-foreground"
            >
              アカウントをお持ちでない方
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* デモアカウント */}
      {IS_DEMO_MODE && (
      <Card className="mt-6 rounded-xl border-border/70 shadow-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">まずは触ってみてください。</CardTitle>
            <Badge variant="outline" className="border-foreground/40 text-foreground">Demo</Badge>
          </div>
          <CardDescription className="leading-5">
            下のデモアカウントをクリックすると、すぐにログインして機能を試せます。ロールごとに管理者画面・スタッフ画面が自動で切り替わります。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-2.5 sm:grid-cols-2">
            {DEMO_ACCOUNTS.map((account) => {
              const Icon = account.icon
              return (
                <button
                  key={account.email}
                  type="button"
                  onClick={() => {
                    setEmail(account.email)
                    setPassword(DEMO_PASSWORD)
                    setMfaCode("")
                    setRequiresMfa(false)
                  }}
                  className="group flex w-full items-start gap-3 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-foreground/40 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground transition-colors group-hover:bg-muted/70">
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-foreground">{account.label}</span>
                    <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">{account.description}</span>
                    <span className="mt-1 block truncate font-mono text-[11px] text-muted-foreground/80">{account.email}</span>
                  </span>
                </button>
              )
            })}
          </div>

          <p className="text-center text-xs text-muted-foreground">
            共通パスワード:{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">{DEMO_PASSWORD}</code>
          </p>
        </CardContent>
      </Card>
      )}

      {/* 未ログインだと / は login に跳ね返されるだけなので、セッションがある時だけ出す */}
      {Boolean(session?.user) && (
        <p className="mt-6 text-center">
          <Link
            href="/"
            className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            ログイン済みの画面を開く
          </Link>
        </p>
      )}
    </AuthLayout>
  )
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <LoginPageContent />
    </Suspense>
  )
}
